/**
 * WebMCP tool registration — exposes the practice's read-only lookups to AI
 * agents browsing the site, and is what Lighthouse's Agentic Browsing audit
 * ("WebMCP Integration") looks for.
 *
 * Current API (Chrome, 2026): document.modelContext.registerTool(tool)
 * Legacy origin-trial API (2025): navigator.modelContext.provideContext({ tools })
 *
 * Registered straight from main.jsx rather than from a component so the tools
 * exist before the audit snapshot. Registration is idempotent and every
 * `execute` is read-only: it returns information or a URL, never submits.
 */

const TOOLS = [
  {
    name: 'get_practice_info',
    description:
      'Get Trinity Dental Arts practice details: doctor, location, phone, hours and website.',
    inputSchema: { type: 'object', properties: {}, required: [] },
    annotations: { readOnlyHint: true },
    execute: async () => ({
      practice: 'Trinity Dental Arts',
      doctor: 'Dr. Xhoana Gjelaj, DMD, MAGD, DABOI/ID, FAAID, DICOI',
      positioning:
        "ABOI/ID board certification in implant dentistry, AGD Mastership, ICOI Diplomate status and AAID Fellowship. Dr. Gjelaj plans treatment around the patient's health, bite and goals.",
      address: '11105 Trinity Blvd, Trinity, FL 34655',
      phone: '(727) 228-6846',
      website: 'https://trinitydentalarts.com',
      hours: 'Monday–Thursday, 9:00 AM – 5:00 PM ET (Friday–Sunday closed)',
      hoursStructured: [
        { day: 'Monday', open: '09:00', close: '17:00', tz: 'America/New_York' },
        { day: 'Tuesday', open: '09:00', close: '17:00', tz: 'America/New_York' },
        { day: 'Wednesday', open: '09:00', close: '17:00', tz: 'America/New_York' },
        { day: 'Thursday', open: '09:00', close: '17:00', tz: 'America/New_York' },
      ],
    }),
  },
  {
    name: 'list_services',
    description:
      'List the dental services offered, optionally filtered by category. Categories: implants, restorative, cosmetic, general, specialty.',
    inputSchema: {
      type: 'object',
      properties: {
        category: {
          type: 'string',
          enum: ['implants', 'restorative', 'cosmetic', 'general', 'specialty'],
          description: 'Optional category filter. Omit to return all services.',
        },
      },
      required: [],
    },
    annotations: { readOnlyHint: true },
    execute: async ({ category } = {}) => {
      const services = [
        { name: 'Dental Implants', category: 'implants', url: 'https://trinitydentalarts.com/dental-implants', description: 'Titanium implant posts that replace missing tooth roots.' },
        { name: 'Single Implant', category: 'implants', url: 'https://trinitydentalarts.com/single-implant', description: 'Replacement of one missing tooth with an individual implant and crown.' },
        { name: 'All-on-X', category: 'implants', url: 'https://trinitydentalarts.com/all-on-x', description: 'Full-arch restoration supported on a set of implants.' },
        { name: 'Fixed New Teeth', category: 'implants', url: 'https://trinitydentalarts.com/fixed-new-teeth', description: "The practice's FixedNewTeeth full-arch fixed implant solution — a non-removable set of teeth." },
        { name: 'Full Mouth Restoration', category: 'implants', url: 'https://trinitydentalarts.com/full-mouth-restoration', description: 'Comprehensive rehabilitation of the full upper and/or lower arches.' },
        { name: 'Bone Grafting', category: 'implants', url: 'https://trinitydentalarts.com/bone-grafting', description: 'Rebuilding jawbone volume to support implants.' },
        { name: 'Sinus Grafts', category: 'implants', url: 'https://trinitydentalarts.com/sinus-grafts', description: 'Adding bone in the upper jaw/sinus area prior to implant placement.' },
        { name: 'Dentures', category: 'restorative', url: 'https://trinitydentalarts.com/dentures', description: 'Removable replacements for missing teeth.' },
        { name: 'Dental Bridges', category: 'restorative', url: 'https://trinitydentalarts.com/dental-bridges', description: 'Fixed restorations that span a gap left by missing teeth.' },
        { name: 'Same Day Crowns', category: 'restorative', url: 'https://trinitydentalarts.com/same-day-crowns', description: 'Custom crowns designed and placed in a single visit.' },
        { name: 'Cosmetic Dentistry', category: 'cosmetic', url: 'https://trinitydentalarts.com/cosmetic-dentistry', description: 'Treatments focused on the appearance of the smile.' },
        { name: 'Invisalign', category: 'cosmetic', url: 'https://trinitydentalarts.com/invisalign', description: 'Clear aligner orthodontic treatment.' },
        { name: 'General Dentistry', category: 'general', url: 'https://trinitydentalarts.com/general-dentistry', description: 'Routine and restorative dental care.' },
        { name: 'Preventative Dentistry', category: 'general', url: 'https://trinitydentalarts.com/preventative-dentistry', description: 'Cleanings, exams, and care aimed at preventing dental disease.' },
        { name: 'Emergency Dentistry', category: 'general', url: 'https://trinitydentalarts.com/emergency-dentistry', description: 'Urgent treatment for dental pain, trauma, or infection.' },
        { name: 'Oral Surgery', category: 'specialty', url: 'https://trinitydentalarts.com/oral-surgery', description: 'Surgical procedures including extractions and implant placement.' },
        { name: 'Sedation Dentistry', category: 'specialty', url: 'https://trinitydentalarts.com/sedation-dentistry', description: 'Comfort-focused options for anxious patients or longer procedures.' },
        { name: 'Laser Dentistry', category: 'specialty', url: 'https://trinitydentalarts.com/laser-dentistry', description: 'Laser-assisted soft-tissue and other treatments.' },
        { name: 'Periodontal Disease Treatment', category: 'specialty', url: 'https://trinitydentalarts.com/periodontal-disease-treatment', description: 'Treatment of gum disease.' },
        { name: 'Regenerative Dentistry', category: 'specialty', url: 'https://trinitydentalarts.com/regenerative-dentistry', description: 'Procedures that support tissue and bone regeneration.' },
      ];
      return category ? services.filter((s) => s.category === category) : services;
    },
  },
  {
    name: 'get_signature_consult_info',
    description:
      'Explain the $300 in-office Signature Consult and return its appointment link.',
    inputSchema: { type: 'object', properties: {}, required: [] },
    annotations: { readOnlyHint: true },
    execute: async () => ({
      name: 'Signature Consult',
      price: '$300',
      terms:
        "This is a 90-minute in-office diagnostic visit. The fee is credited toward treatment begun within 30 days. Follow the office's prepayment instructions.",
      bookingUrl: 'https://trinitydentalarts.com/book-appointment#signature-consult',
    }),
  },
  {
    name: 'get_consult_options',
    description:
      'Learn about the $300 in-office Signature Consultation.',
    inputSchema: { type: 'object', properties: {}, required: [] },
    annotations: { readOnlyHint: true },
    execute: async () => ({
      options: [
        {
          name: 'Signature Consult',
          price: '$300',
          setting: 'In office, Trinity FL',
          length: '90 minutes',
          bookingUrl: 'https://trinitydentalarts.com/book-appointment#signature-consult',
          note: 'Fee is credited toward treatment begun within 30 days.',
        },

      ],
    }),
  },
  {
    name: 'get_booking_page',
    description:
      'Return the appointment page URL for online self-booking, or for a specific visit type.',
    inputSchema: {
      type: 'object',
      properties: {
        visitType: {
          type: 'string',
          enum: ['online-booking', 'signature-consult'],
          description: 'Which booking section to link to. Omit for the general booking page.',
        },
      },
      required: [],
    },
    annotations: { readOnlyHint: true },
    execute: async ({ visitType = 'online-booking' } = {}) => ({
      bookingUrl: `https://trinitydentalarts.com/book-appointment#${visitType}`,
      message: 'Choose an available visit type and time in the self-booking scheduler.',
    }),
  },
];

function getModelContext() {
  if (typeof document === 'undefined') return null;
  return document.modelContext || (typeof navigator !== 'undefined' ? navigator.modelContext : null) || null;
}

/**
 * Registers the tools once per page. Safe to call more than once — Chrome
 * throws on a duplicate name, which we swallow so re-registration is a no-op.
 */
export function registerWebMCPTools() {
  const context = getModelContext();
  if (!context) return;

  if (typeof context.registerTool === 'function') {
    for (const tool of TOOLS) {
      try {
        const result = context.registerTool(tool);
        if (result && typeof result.catch === 'function') result.catch(() => {});
      } catch {
        // Duplicate registration or unsupported tool shape — skip it.
      }
    }
    return;
  }

  // Legacy origin-trial shape, still used by older Chrome builds.
  if (typeof context.provideContext === 'function') {
    try {
      context.provideContext({ tools: TOOLS });
    } catch {
      // Ignore — this is a progressive enhancement.
    }
  }
}